import React, { useCallback, useState } from 'react'
import Tippy from '@tippyjs/react'

import Icon from '../../ui/Icon'

interface CheckboxInputProps {
  id?: string
  name: string
  checked: boolean
  required?: boolean
  className?: string
  label?: React.ReactNode
  disabled?: boolean
  isSwitch?: boolean
  infoMessage?: string
  errorMessage?: string
  onChange: (name: string, value: boolean, isValid: boolean) => void
  onValidate?: (value: boolean) => boolean
}

export const CheckboxInput = ({
  id,
  name,
  checked,
  required = false,
  className = '',
  label,
  disabled = false,
  isSwitch = false,
  infoMessage,
  errorMessage,
  onChange,
  onValidate,
}: CheckboxInputProps) => {
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  const checkboxId = id ?? `checkbox-${name}`
  const isRequired = isTouch && required && !checked

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsTouch(true)
      const currentValue = event.target.checked
      const nativeValid = event.target.validity.valid
      const customValid = onValidate ? onValidate(currentValue) : true
      const valid = nativeValid && customValid
      setIsError(!valid && !(required && !currentValue))
      onChange(name, currentValue, valid)
    },
    [name, onChange, onValidate, required]
  )

  const fieldClass = [
    'checkbox-input',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  const checkboxClass = [
    'checkbox',
    className,
    isSwitch && 'checkbox-switch',
    disabled && 'disabled',
    !isFocus && (isError || isRequired) && 'error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      <label htmlFor={checkboxId} className={checkboxClass}>
        <input
          id={checkboxId}
          type="checkbox"
          name={name}
          checked={checked}
          required={required}
          disabled={disabled}
          aria-invalid={isError || isRequired}
          onChange={handleChange}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
        />

        <span className="box" aria-hidden="true">
          <Icon name="check" size={14} />
        </span>

        {label && <span>{label}</span>}

        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <span className="input-icon-toggle">
              <Icon name="info" size={16} />
            </span>
          </Tippy>
        )}
      </label>

      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>

          {errorMessage}
        </div>
      )}
    </div>
  )
}
